import { useEffect, useState } from "react";
import { format } from "date-fns";
import { Sparkles, ThumbsUp, Target, ExternalLink } from "lucide-react";
import { AttachmentPreviewDrawer } from "@/components/AttachmentPreviewDrawer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Iter196 — Wins Reel widget for the Manager Dashboard.
 *
 * Horizontal carousel of manager-reacted EOD screenshots from the
 * last N hours. Click a card to open the shared
 * `AttachmentPreviewDrawer` — same drawer used in the EOD dialog so
 * reactions stay in sync across the app.
 *
 * Rendered on Manager view only; on empty accounts the widget shows a
 * gentle nudge so managers know how to seed it.
 */
export function WinsReelWidget() {
  const [reel, setReel] = useState([]);
  const [loading, setLoading] = useState(true);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewIndex, setPreviewIndex] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await fetch(`${API}/manager/wins-reel?hours=24&limit=20`, {
          credentials: "include",
        });
        if (!r.ok) return;
        const d = await r.json();
        if (!cancelled) setReel(Array.isArray(d.reel) ? d.reel : []);
      } catch { /* silent */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  const openAt = (i) => { setPreviewIndex(i); setPreviewOpen(true); };

  // AttachmentPreviewDrawer expects rows shaped like the /eod-attachments
  // response — url + content_type + file_id + original_filename + size.
  // Our wins-reel API delivers all of that plus rep-name context.
  const drawerAttachments = reel.map((r) => ({
    file_id: r.file_id,
    url: r.url,
    content_type: r.content_type,
    original_filename: r.original_filename,
    size: 0,
  }));

  return (
    <div
      className="bg-[#111114] border border-white/10 rounded-2xl p-4 sm:p-6 space-y-4"
      data-testid="wins-reel-widget"
    >
      <div className="flex items-center gap-2">
        <Sparkles className="w-4 h-4 text-emerald-400" />
        <h3 className="text-white font-semibold text-base sm:text-lg">Wins Reel</h3>
        <span className="text-xs text-gray-500">last 24 hours</span>
        <span className="ml-auto text-xs text-gray-500">
          {reel.length > 0 && `${reel.length} standout screenshot${reel.length === 1 ? "" : "s"}`}
        </span>
      </div>

      {loading ? (
        <div className="flex gap-3 overflow-x-hidden">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="min-w-[180px] h-[130px] rounded-lg bg-white/5 border border-white/5 animate-pulse" />
          ))}
        </div>
      ) : reel.length === 0 ? (
        <div
          className="text-center py-8 px-4 border border-dashed border-white/10 rounded-xl"
          data-testid="wins-reel-empty"
        >
          <ThumbsUp className="w-6 h-6 text-gray-500 mx-auto mb-2" />
          <p className="text-sm text-gray-400">No standout screenshots yet.</p>
          <p className="text-xs text-gray-500 mt-1">
            React 👍 or 🎯 on an EOD screenshot and it'll show up here for 24 hours.
          </p>
        </div>
      ) : (
        <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1 scrollbar-thin">
          {reel.map((item, i) => {
            const href = `${process.env.REACT_APP_BACKEND_URL}${item.url}`;
            const hasThumbs = item.types.includes("thumbs_up");
            const hasTarget = item.types.includes("target");
            return (
              <button
                key={item.file_id}
                type="button"
                data-testid={`wins-reel-card-${item.file_id}`}
                onClick={() => openAt(i)}
                className="min-w-[180px] w-[180px] shrink-0 group text-left rounded-lg overflow-hidden bg-black/30 border border-white/10 hover:border-emerald-500/50 transition"
              >
                <div className="relative w-full h-[110px] bg-black/40 flex items-center justify-center">
                  <img
                    src={href}
                    alt={item.original_filename}
                    className="max-w-full max-h-full object-contain"
                    loading="lazy"
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                  />
                  <div className="absolute top-1.5 right-1.5 flex items-center gap-1">
                    {hasThumbs && (
                      <span
                        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-emerald-500/20 border border-emerald-500/40 text-emerald-200 text-[10px]"
                      >
                        <ThumbsUp className="w-2.5 h-2.5" />
                        {item.count}
                      </span>
                    )}
                    {hasTarget && (
                      <span
                        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-amber-500/20 border border-amber-500/40 text-amber-200 text-[10px]"
                      >
                        <Target className="w-2.5 h-2.5" />
                        {item.count}
                      </span>
                    )}
                  </div>
                </div>
                <div className="p-2">
                  <div className="text-xs text-white truncate">{item.rep_name}</div>
                  <div className="text-[10px] text-gray-500 flex items-center gap-1">
                    {(() => {
                      try {
                        return format(new Date(item.last_reacted_at), "MMM d, h:mm a");
                      } catch { return ""; }
                    })()}
                    <ExternalLink className="w-2.5 h-2.5 ml-auto opacity-0 group-hover:opacity-100 transition" />
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}

      <AttachmentPreviewDrawer
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        attachments={drawerAttachments}
        initialIndex={previewIndex}
      />
    </div>
  );
}
